import React from 'react';
import qs from 'qs';
import { useHistory, useLocation } from 'react-router-dom';
import InnerHeader from 'src/component/InnerHeader';
import NoxTable from 'src/component/Table';
import Copy from 'src/component/Copy';
import { useGetTrips } from 'src/reactQuery/trips/queries/useGetTrips';
import { Trip } from 'src/services/napi/trips/interfaces/trip.interface';
import { getDataFromLocalStorage } from 'src/localStorage/getDataFromLocalStorage';
import { LocalStorageKey } from 'src/localStorage/enums/localStorageKey.enum';
import { downloadTicketsCsv, downloadTripsCsv } from 'src/services/napi';
import { useDebounce } from 'src/hooks/useDebounce';

const Trips = () => {
  const urlLocation = useLocation();
  const history = useHistory();
  const params = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });

  // const filterList = ['All', 'Not Paid'];

  const { data: tripData, isLoading, refetch: refetchSearch } = useGetTrips({
    orderBy: params.orderBy?.toString(),
    orderDirection: params.orderDirection?.toString(),
    page: params.page?.toString(),
    pageSize: params.pageSize?.toString(),
    search: params.search?.toString()
  });

  useDebounce(() => refetchSearch(), 500, [params.search]);

  const onFilterChange = (status: string) => {
    params.status = status;

    history.push({
      pathname: urlLocation.pathname,
      search: qs.stringify(params)
    });
  };

  async function downloadTripsCSV() {
    const currentProject: any = getDataFromLocalStorage(LocalStorageKey.CURRENT_PROJECT);

    const response: any = await downloadTripsCsv(currentProject._id);

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `trips.csv`);
    document.body.appendChild(link);
    link.click();
  }

  async function downloadTicketsCSV() {
    const currentProject: any = getDataFromLocalStorage(LocalStorageKey.CURRENT_PROJECT);

    const response: any = await downloadTicketsCsv(currentProject._id);

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `trips.csv`);
    document.body.appendChild(link);
    link.click();
  }

  const mapToTable = (trips: Trip[] | undefined) => {
    const data: any[] = [];

    if (!trips || trips.length === 0) {
      return data;
    }

    trips.forEach((trip) => {
      data.push(trip);
    });

    return data;
  };

  const filteredData = mapToTable(tripData?.data);

  const actions = [
    {
      onClick: downloadTripsCSV,
      title: 'Download Trips CSV',
      type: 'primary'
    },
    {
      onClick: downloadTicketsCSV,
      title: 'Download Tickets CSV',
      type: 'primary'
    }
  ];

  const columns = [
    {
      dataIndex: 'id',
      key: 'id',
      render: (name: string) => {
        return (
          <div
            style={{
              color: 'black',
              fontWeight: 700
            }}
          >
            <Copy text={name} />
          </div>
        );
      },
      sorter: true,
      title: 'ID'
    },
    {
      dataIndex: 'provider',
      key: 'provider',
      render: (provider: string) => {
        return <Copy text={provider} />;
      },
      sorter: false,
      title: 'Provider'
    },
    {
      dataIndex: 'startPoint',
      key: 'startPoint',
      render: (startPoint: string) => {
        return <Copy text={startPoint} />;
      },
      sorter: false,
      title: 'Start'
    },
    {
      dataIndex: 'endPoint',
      key: 'endPoint',
      render: (endPoint: number) => {
        return <Copy text={endPoint} />;
      },
      sorter: false,
      title: 'End'
    },
    {
      dataIndex: 'userId',
      key: 'userId',
      render: (userId: string) => {
        return <Copy text={userId} />;
      },
      sorter: true,
      title: 'User ID'
    },
    {
      dataIndex: 'tripType',
      key: 'tripType',
      render: (tripType: number) => {
        return <Copy text={tripType} />;
      },
      sorter: false,
      title: 'Type'
    },
    {
      dataIndex: 'paidCard',
      key: 'paidCard',
      render: (paidCard: string) => {
        return <Copy text={paidCard} />;
      },
      sorter: true,
      title: 'Paid Card'
    },
    {
      dataIndex: 'paidCredit',
      key: 'paidCredit',
      render: (paidCredit: string) => {
        return <Copy text={paidCredit} />;
      },
      sorter: true,
      title: 'Paid Credit'
    },
    {
      dataIndex: 'cardTxId',
      key: 'cardTxId',
      render: (cardTxId: number) => {
        return <Copy text={cardTxId} />;
      },
      sorter: true,
      title: 'Card TX ID'
    },
    {
      dataIndex: 'purchaseCorrelationId',
      key: 'purchaseCorrelationId',
      render: (purchaseCorrelationId: string) => {
        return <Copy text={purchaseCorrelationId} />;
      },
      sorter: false,
      title: 'PurchaseId'
    },
    {
      dataIndex: 'rawPrice',
      key: 'rawPrice',
      render: (rawPrice: string) => {
        return <Copy text={rawPrice} />;
      },
      sorter: false,
      title: 'RawPrice'
    },
    {
      dataIndex: 'departureDate',
      key: 'departureDate',
      render: (departureDate: string) => {
        return <Copy text={departureDate} />;
      },
      sorter: false,
      title: 'Departure'
    },
    {
      dataIndex: 'arrivalDate',
      key: 'arrivalDate',
      render: (arrivalDate: string) => {
        return <Copy text={arrivalDate} />;
      },
      sorter: false,
      title: 'Arrival'
    }
  ];

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={tripData?.paging?.total} title="Trips" />

      {/* @ts-ignore */}
      <NoxTable
        actions={actions}
        checkbox={false}
        columns={columns}
        data={filteredData}
        dragAndDropUrl="content"
        loading={isLoading}
        onFilterChange={onFilterChange}
        totalPages={tripData?.paging?.total}
      />
    </div>
  );
};

export default Trips;
